import { requirementsApi } from './api';
import { scheduleAligned } from '../utils/pollSchedule';
import { isBackendReachable, subscribeConnectionHealth } from './connectionHealth';

// #697: ONE review-activity poller for the whole app.
//
// THE BUG THIS REPLACES, measured in the real app rather than reasoned about: the probe
// ran per hook instance, and each instance issued one HTTP request PER WATCHED PROJECT
// per 10s tick. Four components mount it —
//
//   RequirementsTreeView   every membership   (AppShell never unmounts this view)
//   DashboardHome          every membership
//   ItemDetails            the open item's project (and it renders INSIDE the tree view)
//   ProjectDetails         one project
//
// — so a user in 12 projects, with the tree and the dashboard both alive, produced
// ~144 requests a minute of pure "has anything changed?" traffic. The owner's capture:
// 218 requests in 5.8 minutes. On the current deployment every one of those is a
// network hop to Supabase, so it was paid twice — once in connections, once in round
// trips. It made the dev deployment unusable.
//
// The fix has two halves and BOTH are necessary:
//   1. Subscribers share ONE timer and ONE request. N components watching M projects
//      cost exactly one request per tick, not N x M. (Halving it by de-duplicating the
//      mount sites would not have been enough — the per-project fan-out is the larger
//      term.)
//   2. That request is the batch endpoint, so the union of every subscriber's watched
//      set resolves in one round trip and one grouped query.
//
// Each subscriber still gets its OWN composite marker over its OWN ids, so the
// change-detection semantics per consumer are exactly what they were — a project
// changing in the dashboard's set must not make the item detail view refetch.

// Mirrors `comments_api.MAX_REVIEW_ACTIVITY_PROJECTS`. Kept slightly below the server's
// cap so a client that is one project out never trips a 413 it cannot recover from.
const MAX_IDS_PER_REQUEST = 190;

let nextToken = 1;
const subscribers = new Map();      // token -> { ids: string[], notify: (marker) => void }
const markers = new Map();          // project_id -> last marker string seen
const unauthorized = new Set();     // ids the server refused; kept OUT of later requests
let cancelSchedule = null;
let cancelHealth = null;
let inFlight = false;
let refreshRequested = false;

// Exported for tests and for the visibility handler; not part of the public contract.
export function _reset() {
  subscribers.clear();
  markers.clear();
  unauthorized.clear();
  if (cancelSchedule) cancelSchedule();
  cancelSchedule = null;
  if (cancelHealth) cancelHealth();
  cancelHealth = null;
  inFlight = false;
  refreshRequested = false;
  nextToken = 1;
}

function watchedUnion() {
  const all = new Set();
  subscribers.forEach((s) => s.ids.forEach((id) => { if (!unauthorized.has(id)) all.add(id); }));
  return [...all];
}

// A subscriber's marker is the ordered composite of its own projects' markers. Order
// comes from the subscriber's SORTED ids so the composite is stable across ticks; a
// project with no marker yet contributes '-', never a hole that would shift the rest.
function markerFor(ids) {
  return ids.map((id) => markers.get(id) || '-').join('|');
}

const isVisible = () => typeof document === 'undefined'
  || document.visibilityState !== 'hidden';

async function poll() {
  // HEALTH GATE (owner ask, 2026-07-28) — "always gated on health so that we don't just
  // sit spamming a broken server/connection."
  //
  // This is checked FIRST, ahead of the overlap guard, and deliberately does NOT set
  // `refreshRequested`: there is nothing to catch up on, and queueing a retry here would
  // fire the moment the in-flight request failed, which is the flood this prevents.
  // Recovery is driven by the health subscription in `ensureScheduled` instead, so the
  // resume is triggered by the heartbeat SUCCEEDING rather than by this poller guessing.
  //
  // The probe is the right thing to suppress: it is an enhancement over the reload that
  // already worked, and a probe that cannot reach the server has no answer to give. The
  // heartbeat keeps running (it is the only way to discover recovery) and it backs off,
  // so while the backend is down the client makes one cheap request every 3-30s instead
  // of one probe every 10s plus a preflight each.
  if (!isBackendReachable()) return;
  // Overlap guard: a tick arriving while the previous request is still in flight does
  // not start a second one. Queueing every tick is how a slow backend turns a poller
  // into the storm it was supposed to fix, and a probe has no per-tick value to lose —
  // only the latest answer matters. It is a COALESCE rather than a plain drop, because
  // the call that lost the race may be a newly mounted subscriber whose projects were
  // not in the in-flight request's id set; dropping that outright would leave it with
  // no baseline until the next tick.
  if (inFlight) { refreshRequested = true; return; }
  const ids = watchedUnion();
  if (!ids.length) return;
  inFlight = true;
  try {
    // The endpoint refuses an over-cap set with a 413 rather than truncating it, and
    // this catch is deliberately silent — so an over-cap watcher would lose live refresh
    // PERMANENTLY, invisibly, which is the same silent-staleness failure the whole probe
    // exists to prevent. Chunking keeps it to one request for any real membership count
    // (the cap is far above the dozen that caused #697) and degrades to two rather than
    // to nothing. It does not truncate: every id is still asked about.
    const chunks = [];
    for (let i = 0; i < ids.length; i += MAX_IDS_PER_REQUEST) {
      chunks.push(ids.slice(i, i + MAX_IDS_PER_REQUEST));
    }
    const replies = await Promise.all(
      chunks.map((c) => requirementsApi.getReviewActivityBatch(c)));
    const res = {
      activity: Object.assign({}, ...replies.map((r) => (r && r.activity) || {})),
      unauthorized: replies.flatMap((r) => (r && r.unauthorized) || []),
    };
    const activity = res.activity;
    Object.keys(activity).forEach((pid) => {
      const m = activity[pid] && activity[pid].marker;
      if (m) markers.set(pid, m);
    });
    // Stop asking for ids the server told us we cannot watch. Without this, a stale
    // project id in a subscriber's set is re-sent every 10s forever. They are NAMED by
    // the endpoint rather than silently absent precisely so this is possible.
    ((res && res.unauthorized) || []).forEach((pid) => unauthorized.add(pid));
    // Notify only the subscribers this request actually COVERED.
    //
    // A subscriber that mounted mid-request is watching projects the in-flight call
    // never asked about, so its composite would be built from '-' placeholders — a
    // baseline that is not a real observation. The next tick would then see the real
    // markers, read the difference as an edit, and refetch. That phantom change is
    // exactly what the baseline rule exists to prevent, so an uncovered subscriber
    // waits for a tick that includes it instead of being handed a fabricated one.
    const covered = new Set(ids);
    subscribers.forEach((s) => {
      if (s.ids.every((id) => covered.has(id) || unauthorized.has(id))) {
        s.notify(markerFor(s.ids));
      }
    });
  } catch {
    // Silent by design: live refresh is an enhancement over the reload that already
    // worked, so a failed probe must never surface an error or a spinner. Markers stay
    // where they were and the next tick retries.
  } finally {
    inFlight = false;
    if (refreshRequested) {
      refreshRequested = false;
      poll();
    }
  }
}

function ensureScheduled() {
  if (cancelSchedule || !subscribers.size) return;
  // The review-activity slot is offset from the heartbeat and notification slots — see
  // utils/pollSchedule.js, where a test proves the three tick sets never intersect.
  // Returns poll()'s promise (not fire-and-forgotten) so scheduleAligned's self-pacing
  // (pollSchedule.js) can actually await it before arming the next tick.
  cancelSchedule = scheduleAligned('reviewActivity',
    () => (isVisible() ? poll() : undefined), { minGapMs: 2000 });
  // Resume on recovery rather than waiting out the interval. Standing down is only half
  // the fix: without this the first probe after the backend came back could be a full
  // tick away, and a reviewer would be looking at state that went stale during the
  // outage — the exact staleness the probe exists to prevent, now made WORSE by the gate.
  //
  // ONLY on the TRANSITION back to connected. `connectionHealth` emits on every
  // successful ping (its `consecutiveOk` counter is an event other consumers need), so
  // reacting to `status === 'connected'` alone would fire an extra probe every 15s
  // forever — turning a fix for a request storm into a 50% increase in probe traffic on
  // a perfectly healthy connection. The gate has to remember what it saw last.
  let wasReachable = isBackendReachable();
  cancelHealth = subscribeConnectionHealth(({ status }) => {
    const nowReachable = status === 'connected';
    const recovered = nowReachable && !wasReachable;
    wasReachable = nowReachable;
    if (recovered && isVisible()) poll();
  });
  if (typeof document !== 'undefined' && document.addEventListener) {
    document.addEventListener('visibilitychange', onVisibility);
  }
}

function onVisibility() {
  // Becoming visible is exactly when a reviewer's view is most likely to be stale, so
  // probe immediately rather than waiting out the interval.
  if (isVisible()) poll();
}

function teardownIfIdle() {
  if (subscribers.size) return;
  // Drop the cache when the last subscriber leaves, so a later mount always establishes
  // its baseline from a REAL observation. Keeping it would let a remount take a marker
  // read minutes ago as "current", and the first live tick would then read the interval
  // it slept through as an edit — a change report the user never saw happen.
  markers.clear();
  unauthorized.clear();
  if (cancelSchedule) cancelSchedule();
  cancelSchedule = null;
  // Unsubscribe from health too, or a poller with no subscribers would still wake on
  // every reconnect and issue a request for a watched set that no longer exists.
  if (cancelHealth) cancelHealth();
  cancelHealth = null;
  if (typeof document !== 'undefined' && document.removeEventListener) {
    document.removeEventListener('visibilitychange', onVisibility);
  }
}

/**
 * Watch a set of projects. Returns an unsubscribe function.
 *
 * `notify` is called with THIS subscriber's composite marker after every tick that
 * produced data — including the first, which the caller must treat as a baseline and
 * not as a change.
 *
 * @param {string[]} ids sorted project ids this subscriber cares about
 * @param {(marker: string) => void} notify
 */
export function subscribeReviewActivity(ids, notify) {
  const token = nextToken;
  nextToken += 1;
  subscribers.set(token, { ids, notify });
  ensureScheduled();
  // Probe immediately for a NEW watched set rather than waiting out the interval: a
  // freshly mounted consumer has no baseline at all, and the aligned grid means its
  // first tick could otherwise be a full interval away.
  if (ids.some((id) => !markers.has(id))) {
    poll();
  } else {
    // Everything this subscriber watches is already known — hand it the baseline now
    // instead of leaving it blank until the next tick.
    notify(markerFor(ids));
  }
  return () => {
    subscribers.delete(token);
    teardownIfIdle();
  };
}

export default subscribeReviewActivity;
